import { Box, Link, Paper, TableCell, TableRow, Typography } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link as RRLink, useLocation } from 'react-router-dom'
import { TableLabelText, TablePagination, TablePaginationActions } from '~/src/components/molecules'
import { TableListContainer } from '~/src/components/organisms'
import { formatDate } from '~/src/libs/formatDate'

type Params = {
  limit: number
  offset?: number
  keyword?: string
  userId?: number
}

interface Column {
  id: 'company' | 'name' | 'date'
  label: string
  minWidth?: number | string
  align?: 'right' | 'left' | 'center'
  alignItem?: 'right' | 'left' | 'center'
  render: (item: IOrderStatementOfAccount, index: number) => React.ReactNode
}

interface IPagination {
  currentPage: number
  limitValue: number
  totalCount: number
  totalPages: number
}

interface IOrderStatementOfAccount {
  company: string
  name: string
  date: string
  id: number
}

interface ITableContainer {
  data: IOrderStatementOfAccount[]
  pagination?: IPagination | undefined
  onPageChange: (_params?: Params) => void
}

/**
 * TableStatementOfAccountList
 * @param data
 * @constructor
 */
export const TableStatementOfAccountList: React.FC<ITableContainer> = ({
  data,
  pagination,
  onPageChange,
}): JSX.Element => {
  const { t } = useTranslation()
  const location = useLocation()
  const [page, setPage] = useState((pagination?.currentPage ?? 0) - 1)

  useEffect(() => {
    if (pagination?.currentPage !== page) {
      // @ts-ignore
      setPage(pagination?.currentPage - 1)
    }
  }, [pagination])

  const handleChangePage = (event: unknown, newPage: number): void => {
    console.log({ event, newPage })
    setPage(newPage)
    onPageChange({ offset: newPage + 1, limit: 20 })
  }

  const columns: Column[] = [
    {
      id: 'company',
      label: `${t('statement_of_account_list.company_name')}`,
      minWidth: '70%',
      render: (row) => (
        <Typography
          variant={'body2'}
          sx={{
            fontFamily: 'Roboto',
            fontStyle: 'normal',
            fontWeight: 400,
            fontSize: '14px',
            lineHeight: '20px',
            letterSpacing: '0.15px',
          }}
        >
          {row.company}
        </Typography>
      ),
    },
    {
      id: 'name',
      label: `${t('statement_of_account_list.customer')}`,
      minWidth: '20%',
      render: ({ name }) => <TableLabelText>{name}</TableLabelText>,
    },
    {
      id: 'date',
      label: `${t('statement_of_account_list.created_date')}`,
      minWidth: '10%',
      align: 'left',
      alignItem: 'right',
      render: ({ date }) => <TableLabelText>{formatDate(date)}</TableLabelText>,
    },
  ]
  return (
    <Paper sx={{ width: '100%', mb: 4, mt: 4 }}>
      <TableListContainer
        tableHeadComponents={columns.map((column, index) => (
          <TableCell
            key={column.id}
            sortDirection={'desc'}
            style={{
              minWidth: column.minWidth,
              textAlign: column.align,
              background: 'white',
              height: '56px',
            }}
          >
            <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'}>
              <Typography
                sx={{
                  fontSize: '14px',
                  lineHeight: '20px',
                  fontFamily: 'Roboto',
                  fontStyle: 'normal',
                  fontWeight: 400,
                }}
              >
                {column.label}
              </Typography>
              <Typography
                sx={{
                  height: '14px',
                  width: '2px',
                  backgroundColor: index === columns.length - 1 ? 'white' : '#E0E0E0',
                }}
              />
            </Box>
          </TableCell>
        ))}
        tableBodyComponents={data.map((row, index) => {
          return (
            <TableRow hover role="checkbox" tabIndex={-1} key={index}>
              {columns.map((column) => (
                <TableCell key={column.id} style={{ textAlign: column.align, padding: '13.5px' }}>
                  <Link
                    to={`/admin/order/statement-of-account/detail?id=${row?.id}`}
                    state={{
                      item: row,
                      prevLocationState: { ...location }
                    }}
                    underline="hover"
                    component={RRLink}
                  >
                    {column.render(row, index)}
                  </Link>
                </TableCell>
              ))}
            </TableRow>
          )
        })}
        tableContainerSx={{ padding: 1, paddingBottom: 2 }}
      />
      <TablePagination
        count={pagination ? pagination?.totalCount : 0}
        rowsPerPage={pagination ? pagination?.limitValue : 20}
        page={page}
        onPageChange={handleChangePage}
        ActionsComponent={TablePaginationActions}
      />
    </Paper>
  )
}
